// Joana chat shell — the TRUSTED payment confirm sheet. When an untrusted
// mini-app calls `payments.intent`, the worker broker proxies it to the main
// thread (it's UI-bearing), and THIS presents a host-owned modal: the user picks
// a provider + enters their number + approves, then the shell charges through
// Phoenix `/payments/initiate` → the Rust payments service. The mini-app states
// only WHAT to charge and receives only the RESULT — it never sees the provider,
// the phone number, or any PIN/checkout step (the WeChat payments model, mirrors
// the KMP PaymentsIntentCapability + host PaymentIntentPort).
//
// DOM-agnostic: it drives the SAME ViewHost/Renderer pipeline as the chat surface
// (rendered THROUGH the Joana render layer — even this trusted chrome is catalog,
// not raw HTML), so it runs in Node tests (RecordingRenderer) and the browser
// (DomRenderer into #payment-layer) unchanged.

import { hostTranslator } from "../i18n/index.ts";
import type { VAction, VNode } from "../render/catalog.ts";
import type { RenderEvent } from "../render/patch.ts";
import { applyFrame } from "../render/renderer.ts";
import { ViewHost } from "../render/view-host.ts";
import type { RendererFactory } from "./surface.ts";
import type {
  PaymentApproval,
  PaymentIntentRequest,
  PaymentIntentResult,
} from "./types.ts";

export interface ProviderOption {
  value: string;
  label: string;
}

/** Tanzania's mobile-money rails — the provider strings Phoenix's
 *  `provider_enum` accepts (services/api/lib/sema_api/payments.ex). */
/**
 * The sheet's own translator (ADR-0028).
 *
 * It reads the stored choice directly rather than taking one from the shell:
 * the sheet is presented BY a mini-app, over the shell, and a payment
 * confirmation is the last screen that should be in a different language from
 * the app around it. Read per present() rather than once at module load, so a
 * language changed mid-session reaches the next sheet.
 */
const sheetTranslator = hostTranslator;

/**
 * A FUNCTION and not a const, because the labels are read through the
 * catalogue and a module-level const is built once, before anybody has chosen
 * a language. The `value` side is a wire enum and is never translated: a
 * translated provider id is a failed payment, not a mistranslation.
 *
 * The names themselves are brands and a translator will almost always leave
 * them alone. They are listed anyway, because "this one does not need
 * translating" is a judgement for the person who speaks the language.
 */
export function defaultProviders(
  t: (key: string, ...args: (string | number)[]) => string,
): ProviderOption[] {
  return [
    { value: "mpesa", label: t("M-Pesa") },
    { value: "airtel_money", label: t("Airtel Money") },
    { value: "tigopesa", label: t("Mixx by Yas") },
  ];
}

export interface PresentOptions {
  appTitle: string;
  request: PaymentIntentRequest;
  providers?: ProviderOption[];
  /** Performs the actual charge once the user approves. Throwing surfaces inline
   *  in the sheet (the user can retry or cancel); resolving closes the sheet and
   *  resolves `present()`. The shell wires this to `ApiClient.initiatePayment`. */
  charge: (approval: PaymentApproval) => Promise<PaymentIntentResult>;
}

type Phase = "input" | "processing" | "error";

interface SheetState {
  visible: boolean;
  appTitle: string;
  request: PaymentIntentRequest | null;
  providers: ProviderOption[];
  selectedProvider: string | null;
  phone: string;
  phase: Phase;
  error: string | null;
}

const HIDDEN: SheetState = {
  visible: false,
  appTitle: "",
  request: null,
  providers: [],
  selectedProvider: null,
  phone: "",
  phase: "input",
  error: null,
};

/** Thrown when the user dismisses the sheet — the broker turns it into the
 *  mini-app's rejected `payments.intent` promise. */
export class PaymentCancelled extends Error {
  constructor() {
    super("payment cancelled");
    this.name = "PaymentCancelled";
  }
}

export class PaymentSheetController {
  private readonly host = new ViewHost();
  private readonly renderer;
  private state: SheetState = HIDDEN;
  private pending: {
    resolve: (r: PaymentIntentResult) => void;
    reject: (e: Error) => void;
    charge: PresentOptions["charge"];
  } | null = null;

  /** Toggle the host layer's visibility (the controller never touches DOM). */
  private readonly onVisibility: (visible: boolean) => void;

  constructor(
    rendererFactory: RendererFactory,
    onVisibility: (visible: boolean) => void = () => {},
  ) {
    this.onVisibility = onVisibility;
    this.renderer = rendererFactory((e) => this.onEvent(e));
  }

  get isOpen(): boolean {
    return this.state.visible;
  }

  /** Present the sheet for one intent. Resolves with the charge result on
   *  approve+success; rejects with {@link PaymentCancelled} on dismiss. Only one
   *  intent at a time — a second call while open cancels the first. */
  present(opts: PresentOptions): Promise<PaymentIntentResult> {
    if (this.pending) this.cancel();
    const providers = opts.providers && opts.providers.length > 0
      ? opts.providers
      : defaultProviders(sheetTranslator());
    return new Promise<PaymentIntentResult>((resolve, reject) => {
      this.pending = { resolve, reject, charge: opts.charge };
      this.state = {
        visible: true,
        appTitle: opts.appTitle,
        request: opts.request,
        providers,
        selectedProvider: providers[0]?.value ?? null,
        phone: "",
        phase: "input",
        error: null,
      };
      this.onVisibility(true);
      this.render();
    });
  }

  private onEvent(e: RenderEvent): void {
    const action = this.host.actionFor(e);
    if (!action || action.intent !== "pay") return;
    const payload = { ...(action.payload ?? {}), ...(e.payload ?? {}) } as Record<string, unknown>;
    switch (String(payload.cmd ?? "")) {
      case "select_provider":
        return this.set({ selectedProvider: String(payload.provider), error: null });
      case "set_phone":
        return this.set({ phone: String(payload.value ?? "") });
      case "pay":
        return void this.confirm();
      case "cancel":
        return this.cancel();
    }
  }

  private async confirm(): Promise<void> {
    if (this.state.phase === "processing" || !this.pending || !this.state.request) return;
    if (!this.state.selectedProvider) {
      this.set({ error: "Choose a payment method" });
      return;
    }
    const phone = normalizePhone(this.state.phone);
    if (!isValidPhone(phone)) {
      this.set({ error: "Enter a valid phone number" });
      return;
    }
    const charge = this.pending.charge;
    this.set({ phase: "processing", error: null });
    try {
      const result = await charge({ provider: this.state.selectedProvider, phone });
      const pending = this.pending;
      this.pending = null;
      this.dismiss();
      pending?.resolve(result);
    } catch (e) {
      // Stay open so the user can retry or cancel.
      this.set({ phase: "error", error: e instanceof Error ? e.message : String(e) });
    }
  }

  private cancel(): void {
    const pending = this.pending;
    this.pending = null;
    this.dismiss();
    pending?.reject(new PaymentCancelled());
  }

  private dismiss(): void {
    this.state = { ...this.state, visible: false };
    this.onVisibility(false);
    this.render();
  }

  private set(patch: Partial<SheetState>): void {
    this.state = { ...this.state, ...patch };
    this.render();
  }

  private render(): void {
    applyFrame(this.renderer, this.host.render(paymentSheetTree(this.state)));
  }
}

// --- pure view -------------------------------------------------------------

function pay(cmd: string, extra: Record<string, unknown> = {}): VAction {
  return { intent: "pay", payload: { cmd, ...extra } };
}

// ROLE NAMES, never hexes — the same rule the chat shell follows. This sheet
// is drawn OVER a running mini-app and was the last part of the trusted surface
// still painted in literals: it had no dark appearance at all, and the widened
// token lint is what finally said so.
const INK = "text.primary";
const MUTED = "text.secondary";

/** Pure state→tree. Always returns a stable root id so the diff keeps identity;
 *  when hidden it's an empty root (the host layer is also display:none'd). */
export function paymentSheetTree(state: SheetState): VNode {
  if (!state.visible || !state.request) {
    return { type: "stack", id: "ps-root", style: {}, children: [] };
  }
  // ADR-0028 — the confirmation reads in the same language as the app around
  // it. Resolved per render rather than held, so a language changed while the
  // sheet is open takes effect on the next frame.
  const t = sheetTranslator();
  const req = state.request;
  const processing = state.phase === "processing";
  return {
    type: "stack",
    id: "ps-root",
    style: { direction: "column", justify: "flex-end", align: "stretch", width: "100%", height: "100%" },
    children: [
      {
        type: "stack",
        id: "ps-card",
        style: {
          direction: "column",
          gap: 12,
          padding: "20 18 18 18",
          background: "surface",
          radius: 18,
        },
        children: [
          { type: "text", id: "ps-title", props: { value: t("Confirm payment") }, style: { font_size: 13, font_weight: 700, color: MUTED, text_align: "center" } },
          { type: "text", id: "ps-app", props: { value: t("%s requests", state.appTitle) }, style: { font_size: 13, color: MUTED, text_align: "center" } },
          { type: "text", id: "ps-amount", props: { value: `${req.currency} ${groupDigits(req.amount)}` }, style: { font_size: 34, font_weight: 800, color: INK, text_align: "center" } },
          ...(req.description
            ? [{ type: "text" as const, id: "ps-desc", props: { value: req.description }, style: { font_size: 14, color: MUTED, text_align: "center" } }]
            : []),
          { type: "text", id: "ps-pick", props: { value: t("Pay with") }, style: { font_size: 13, font_weight: 600, color: INK, margin: "6 0 0 0" } },
          {
            type: "stack",
            id: "ps-providers",
            style: { direction: "row", gap: 8 },
            children: state.providers.map((p) => providerButton(p, p.value === state.selectedProvider)),
          },
          {
            type: "input",
            id: "ps-phone",
            props: { placeholder: t("Phone number (e.g. 0712 345 678)"), value: state.phone, input_type: "tel", disabled: processing },
            style: { padding: 13, radius: 10, border: "1px solid outline.variant", font_size: 16, background: "surface.variant", color: INK, margin: "4 0 0 0" },
            events: { input: pay("set_phone") },
          },
          ...(state.error
            ? [{ type: "text" as const, id: "ps-error", props: { value: state.error }, style: { font_size: 13, color: "danger", text_align: "center" } }]
            : []),
          {
            type: "stack",
            id: "ps-actions",
            style: { direction: "row", gap: 10, margin: "4 0 0 0" },
            children: [
              {
                type: "button",
                id: "ps-cancel",
                props: { label: t("Cancel"), disabled: processing },
                style: { flex: 1, padding: 13, radius: 10, background: "surface.variant", color: INK, font_size: 16, font_weight: 600, border: "none", opacity: processing ? 0.6 : 1 },
                action: pay("cancel"),
              },
              {
                type: "button",
                id: "ps-pay",
                props: { label: processing ? t("Processing…") : t("Pay %s %s", req.currency, groupDigits(req.amount)), disabled: processing },
                style: { flex: 2, padding: 13, radius: 10, background: "brand.gradient.end", color: "brand.on.bar", font_size: 16, font_weight: 700, border: "none", opacity: processing ? 0.7 : 1 },
                action: pay("pay"),
              },
            ],
          },
        ],
      },
    ],
  };
}

function providerButton(p: ProviderOption, selected: boolean): VNode {
  return {
    type: "button",
    id: `ps-prov-${p.value}`,
    props: { label: p.label },
    style: {
      flex: 1,
      padding: "10 8",
      radius: 10,
      font_size: 14,
      font_weight: 600,
      border: selected ? "2px solid accent" : "1px solid outline.variant",
      background: selected ? "accent.wash" : "surface",
      color: selected ? "accent" : INK,
    },
    action: pay("select_provider", { provider: p.value }),
  };
}

// --- helpers ---------------------------------------------------------------

/** Validate + reduce a mini-app's `payments.intent` args into a request. Throws a
 *  plain Error (the broker turns it into the app's rejected promise) — mirrors the
 *  KMP capability's amount checks. */
export function parsePaymentIntent(args: Record<string, unknown>): PaymentIntentRequest {
  const amount = Number(args.amount);
  if (!Number.isFinite(amount) || !Number.isInteger(amount)) {
    throw new Error("payments.intent requires an integer amount");
  }
  if (amount <= 0) throw new Error("payments.intent amount must be positive");
  return {
    amount,
    description: typeof args.description === "string" ? args.description : "",
    currency: typeof args.currency === "string" && args.currency ? args.currency : "TZS",
    payee: typeof args.payee === "string" ? args.payee : null,
  };
}

function normalizePhone(raw: string): string {
  return raw.replace(/[\s-()]/g, "");
}

function isValidPhone(phone: string): boolean {
  return /^\+?\d{9,15}$/.test(phone);
}

function groupDigits(n: number): string {
  const s = Math.trunc(Math.abs(n)).toString();
  const grouped = s.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return n < 0 ? `-${grouped}` : grouped;
}
